import type { SortDirection } from "@/components/ui/SortableTableHeader";

import { apInvoiceListVendorInvoiceLabel } from "./apInvoiceStatusUi";
import { formatApInvoiceLinkedTshMvNumber } from "./apInvoiceTshUi";
import type { ApInvoiceRow } from "./apInvoicesApi";

export type ApInvoiceListSortKey =
  | "vendor_invoice"
  | "invoice_date"
  | "vendor"
  | "purchase_order"
  | "amount"
  | "receipts"
  | "megaventory"
  | "quickbooks";

export const AP_INVOICE_LIST_DEFAULT_SORT_KEY: ApInvoiceListSortKey = "invoice_date";
export const AP_INVOICE_LIST_DEFAULT_SORT_DIR: SortDirection = "desc";

function compareStrings(left: string, right: string, direction: SortDirection): number {
  const cmp = left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });
  return direction === "asc" ? cmp : -cmp;
}

function compareNumbers(left: number, right: number, direction: SortDirection): number {
  const cmp = left - right;
  return direction === "asc" ? cmp : -cmp;
}

function invoiceDateSortRank(raw: string | null, direction: SortDirection): number {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") {
    return direction === "desc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  const parsed = new Date(trimmed.includes("T") ? trimmed : `${trimmed}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return direction === "desc" ? Number.POSITIVE_INFINITY - 1 : Number.NEGATIVE_INFINITY + 1;
  }
  return parsed.getTime();
}

function compareInvoiceDates(
  left: string | null,
  right: string | null,
  direction: SortDirection,
): number {
  const leftRank = invoiceDateSortRank(left, direction);
  const rightRank = invoiceDateSortRank(right, direction);
  return compareNumbers(leftRank, rightRank, direction);
}

function parseAmount(raw: string | null): number {
  if (raw == null || raw.trim() === "") {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function megaventorySortLabel(row: ApInvoiceRow): string {
  const gipi = (row.purchase_invoice_mv_number ?? "").trim();
  const tsh = formatApInvoiceLinkedTshMvNumber(row.tsh_purchase_invoice_mv_number);
  return [gipi, tsh].filter((part) => part !== "").join(" ");
}

function quickBooksSortLabel(row: ApInvoiceRow): string {
  const parts = [
    row.purchase_invoice_qb_id?.trim() ?? "",
    row.qbo_bill_id?.trim() ?? "",
    row.qbo_match_confirmed_at?.trim() ?? "",
  ].filter((part) => part !== "");
  return parts.join("|");
}

function receiptsSortLabel(row: ApInvoiceRow): string {
  if (row.inbound_receipt_id != null) {
    return row.inbound_receipt_id;
  }
  return (row.receipt_match_status ?? "").trim();
}

export function compareApInvoiceListRows(
  left: ApInvoiceRow,
  right: ApInvoiceRow,
  sortKey: ApInvoiceListSortKey,
  direction: SortDirection,
): number {
  let cmp = 0;
  switch (sortKey) {
    case "vendor_invoice":
      cmp = compareStrings(apInvoiceListVendorInvoiceLabel(left), apInvoiceListVendorInvoiceLabel(right), direction);
      break;
    case "invoice_date":
      cmp = compareInvoiceDates(left.invoice_date, right.invoice_date, direction);
      break;
    case "vendor":
      cmp = compareStrings((left.supplier_name ?? "").trim(), (right.supplier_name ?? "").trim(), direction);
      break;
    case "purchase_order":
      cmp = compareStrings(
        (left.purchase_order_number ?? left.purchase_order_id ?? "").trim(),
        (right.purchase_order_number ?? right.purchase_order_id ?? "").trim(),
        direction,
      );
      break;
    case "amount":
      cmp = compareNumbers(parseAmount(left.total_amount), parseAmount(right.total_amount), direction);
      break;
    case "receipts":
      cmp = compareStrings(receiptsSortLabel(left), receiptsSortLabel(right), direction);
      break;
    case "megaventory":
      cmp = compareStrings(megaventorySortLabel(left), megaventorySortLabel(right), direction);
      break;
    case "quickbooks":
      cmp = compareStrings(quickBooksSortLabel(left), quickBooksSortLabel(right), direction);
      break;
    default: {
      const _exhaustive: never = sortKey;
      return _exhaustive;
    }
  }

  if (cmp !== 0) {
    return cmp;
  }
  return compareNumbers(Number.parseInt(left.id, 10), Number.parseInt(right.id, 10), "desc");
}

export function sortApInvoiceListRows(
  rows: readonly ApInvoiceRow[],
  sortKey: ApInvoiceListSortKey,
  direction: SortDirection,
): ApInvoiceRow[] {
  return [...rows].sort((left, right) => compareApInvoiceListRows(left, right, sortKey, direction));
}

export function defaultSortDirectionForApInvoiceListColumn(sortKey: ApInvoiceListSortKey): SortDirection {
  return sortKey === "invoice_date" ? "desc" : "asc";
}
